import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ModalPortal } from '../modal/modal-portal';
import { useDialogEscape } from '../hooks/use-dialog-focus';
import { AppButton } from '../button/app-button';
import { m, overlayMotion, scrimMotion } from '../../motion';
import { palette, radius, shadow, spacing, zIndex } from '../../style-utils/theme';
import {
  ViewerBar,
  ViewerClose,
  ViewerCount,
  ViewerImage,
  ViewerScrim,
  ViewerStage,
} from './entity-gallery.styled';

/**
 * Full-size viewer with zoom and pan.
 *
 * Deliberately NOT react-easy-crop reused in a read-only mode. That component
 * owns its gestures in service of producing a crop rectangle, and bending it
 * into a viewer means fighting it for wheel and pointer events. Sixty lines of
 * transform maths is the smaller thing to maintain.
 *
 * Zoom is applied as a CSS transform on the <img>, not by swapping in a larger
 * source: the stored image is 1200x1200 and that is all there is, so zooming
 * past 1x is magnification of what we have. Pretending otherwise would mean
 * storing an original we deliberately do not keep.
 *
 * MOTION. The scrim, the close button and the toolbar fade; the stage grows
 * from the centre ('modal', static - a lightbox stays centred at every width).
 * The exit is the entrance reversed: the gallery mounts this inside a
 * Presence, and this component's motion roots carry the exit variants.
 */

const ZOOM_STEP = 0.5;
const ZOOM_MIN = 1;
const ZOOM_MAX = 4;

/* The optional action row (the stay guide's "Share photo", plan S7.7): its own
   pill above the zoom bar, so a 320px phone never squeezes both into one line.
   Clear of the home bar through var(--safe-bottom). */
const ViewerActions = m.div`
  position: fixed;
  left: 50%;
  bottom: calc(${spacing.xxl} + ${spacing.lg} + var(--safe-bottom));
  transform: translateX(-50%);
  z-index: ${zIndex.modal};
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  max-width: calc(100vw - ${spacing.lg});
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.md};
  background: ${palette.surface};
  box-shadow: ${shadow.lg};
`;

/** What the viewer needs of a photo: an EntityGallery image or a stay guide photo. */
export interface ViewerImageSource {
  id: string;
  url: string;
}

interface ImageViewerProps {
  images: ReadonlyArray<ViewerImageSource>;
  startIndex: number;
  onClose: () => void;
  /** Replaces the "2 / 7" count ("Photo 2 of 7"). Shown even for one photo. */
  countLabel?: (index: number, total: number) => string;
  /** The photo's alt text (default ''). Never a door code. */
  altOf?: (index: number) => string;
  /** Called with the photo now shown (on open and on every step). */
  onIndexChange?: (index: number) => void;
  /** An action row for the photo shown ("Share photo"). */
  actions?: (index: number) => React.ReactNode;
}

export const ImageViewer: React.FC<ImageViewerProps> = ({
  images,
  startIndex,
  onClose,
  countLabel,
  altOf,
  onIndexChange,
  actions,
}) => {
  const [index, setIndex] = useState(startIndex);
  const [scale, setScale] = useState(ZOOM_MIN);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ x: number; y: number } | null>(null);
  const onIndexRef = useRef(onIndexChange);
  onIndexRef.current = onIndexChange;

  useDialogEscape(true, onClose);

  useEffect(() => {
    if (onIndexRef.current) onIndexRef.current(index);
  }, [index]);

  const reset = useCallback(() => {
    setScale(ZOOM_MIN);
    setOffset({ x: 0, y: 0 });
  }, []);

  const step = useCallback(
    (delta: number) => {
      setIndex((current) => {
        const next = (current + delta + images.length) % images.length;

        return next;
      });
      reset();
    },
    [images.length, reset]
  );

  // Arrow keys move between photos. A gallery viewer without them feels broken
  // to anyone who has used any other one.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') step(1);
      if (event.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [step]);

  const zoomTo = (next: number) => {
    const clamped = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next));
    setScale(clamped);
    // Snapping back to centre at 1x is not cosmetic: a pan applied while zoomed
    // in would otherwise leave the image visibly off-centre once it fits again,
    // with no way to tell why.
    if (clamped === ZOOM_MIN) setOffset({ x: 0, y: 0 });
  };

  const onPointerDown = (event: React.PointerEvent<HTMLImageElement>) => {
    if (scale === ZOOM_MIN) return;
    dragRef.current = { x: event.clientX - offset.x, y: event.clientY - offset.y };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLImageElement>) => {
    if (!dragRef.current) return;
    setOffset({
      x: event.clientX - dragRef.current.x,
      y: event.clientY - dragRef.current.y,
    });
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  const image = images[index];
  if (!image) return null;

  return (
    <ModalPortal>
      <ViewerScrim
        {...scrimMotion}
        onMouseDown={(event) =>
          event.target === event.currentTarget && onClose()
        }
      >
        <ViewerStage {...overlayMotion('modal')}>
          <ViewerImage
            src={image.url}
            alt={altOf ? altOf(index) : ''}
            draggable={false}
            $scale={scale}
            $x={offset.x}
            $y={offset.y}
            onClick={() => scale === ZOOM_MIN && zoomTo(ZOOM_MIN + ZOOM_STEP)}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onWheel={(event) =>
              zoomTo(scale + (event.deltaY < 0 ? 0.2 : -0.2))
            }
          />
        </ViewerStage>
      </ViewerScrim>

      <ViewerClose
        {...scrimMotion}
        type="button"
        onClick={onClose}
        aria-label="Close viewer"
      >
        ×
      </ViewerClose>

      <ViewerBar {...scrimMotion}>
        {images.length > 1 ? (
          <AppButton
            variant="ghost"
            size="compact"
            onClick={() => step(-1)}
            aria-label="Previous photo"
          >
            ‹
          </AppButton>
        ) : null}
        <AppButton
          variant="ghost"
          size="compact"
          onClick={() => zoomTo(scale - ZOOM_STEP)}
          disabled={scale <= ZOOM_MIN}
          aria-label="Zoom out"
        >
          −
        </AppButton>
        <ViewerCount>
          {countLabel
            ? countLabel(index, images.length)
            : images.length > 1
            ? `${index + 1} / ${images.length}`
            : `${Math.round(scale * 100)}%`}
        </ViewerCount>
        <AppButton
          variant="ghost"
          size="compact"
          onClick={() => zoomTo(scale + ZOOM_STEP)}
          disabled={scale >= ZOOM_MAX}
          aria-label="Zoom in"
        >
          +
        </AppButton>
        {images.length > 1 ? (
          <AppButton
            variant="ghost"
            size="compact"
            onClick={() => step(1)}
            aria-label="Next photo"
          >
            ›
          </AppButton>
        ) : null}
      </ViewerBar>

      {actions ? <ViewerActions {...scrimMotion}>{actions(index)}</ViewerActions> : null}
    </ModalPortal>
  );
};
